import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { RoutineAssignment, ClassMember, User } from '../types/alarm';
import { routineAssignmentService } from '../services/routineAssignmentService';
import { userDataService } from '../services/userDataService';
import { checkAssignmentTypeLimit, isStandardPlanOrAbove } from '../utils/licenseLimits';
import LicenseLimitModal from './LicenseLimitModal';
import { generateAssignmentId } from '../utils/idGenerator';
import ConfirmModal from './ConfirmModal';
import ItemCardSelectionModal from './ItemCardSelectionModal';
import { ItemCardService } from '../services/itemCardService';
import { ItemCard } from '../types/itemCard';
import { getOrderedMemberGroups } from '../utils/memberGroupOrder';

// FormSection 스타일 컴포넌트 (컴포넌트 함수 위에 정의)
const FormSection = styled.div`
  background: var(--color-section-bg);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid var(--color-section-border);
  
  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 16px;
  }
`;

const GroupControls = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin: 10px 0 6px;
  flex-wrap: wrap;
`;

const GroupTabs = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const GroupTabButton = styled.button<{ $active?: boolean }>`
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: ${p => (p.$active ? 'var(--color-accent-strong)' : 'var(--color-panel)')};
  color: ${p => (p.$active ? 'white' : 'var(--color-text-primary)')};
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
`;

interface RoutineAssignmentSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (routineAssignment: any) => void;
  members: ClassMember[];
  memberGroups?: string[];
  memberGroupOrder?: string[];
  currentMemberGroup?: string;
  memberGroupNames?: { [groupKey: string]: string };
  mode?: 'new' | 'copy' | 'edit';
  initialData?: Partial<RoutineAssignment>;
  initialSelectedIds?: string[];
  currentClassId: string;
  userId: string;
  user: User; // 사용자 정보 추가 (라이센스 체크용)
  defaultPoints?: number; // 기본 포인트 단위
  onLicenseClick?: () => void; // 이용권 구매 모달 열기
}

const RoutineAssignmentSettingsModal: React.FC<RoutineAssignmentSettingsModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  members, 
  memberGroups,
  memberGroupOrder,
  currentMemberGroup = 'A',
  memberGroupNames,
  mode = 'new', 
  initialData, 
  initialSelectedIds,
  currentClassId,
  userId,
  user,
  defaultPoints = 10,
  onLicenseClick
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState(''); // description 상태 추가
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [basePointsDisplay, setBasePointsDisplay] = useState<string>(() => String(defaultPoints));
  const getBasePointsNum = () => (basePointsDisplay === '' ? 0 : (Number(basePointsDisplay) || 0));
  const [routineDays, setRoutineDays] = useState<number[]>([]);
  const [icon, setIcon] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [rewardEnabled, setRewardEnabled] = useState(false);
  const [includeInIncompleteCheck, setIncludeInIncompleteCheck] = useState(false);
  const [rewardMessage, setRewardMessage] = useState('');
  const [rewardCoinsDisplay, setRewardCoinsDisplay] = useState<string>('');
  const getRewardCoinsNum = () => (rewardCoinsDisplay === '' ? 0 : (Number(rewardCoinsDisplay) || 0));
  const [rewardItemCardIds, setRewardItemCardIds] = useState<string[]>([]);
  const [rewardCompletionRate, setRewardCompletionRate] = useState<number>(100);
  const [isItemCardModalOpen, setIsItemCardModalOpen] = useState(false);
  const [selectedItemCards, setSelectedItemCards] = useState<ItemCard[]>([]);
  const [rewardCardImageErrors, setRewardCardImageErrors] = useState<Set<string>>(new Set());
  const normalizedGroups = useMemo(() => {
    let gs = getOrderedMemberGroups(memberGroups, memberGroupOrder);

    if (!isStandardPlanOrAbove(user.userType)) {
      gs = ['A'];
    }

    return gs.length > 0 ? gs : ['A'];
  }, [memberGroups, memberGroupOrder, user.userType]);

  const [activeGroup, setActiveGroup] = useState<string>('A');
  const [membersByGroup, setMembersByGroup] = useState<Record<string, ClassMember[]>>({});
  const [selectedByGroup, setSelectedByGroup] = useState<Record<string, Set<string>>>({});
  const [selectionLoadedGroups, setSelectionLoadedGroups] = useState<Set<string>>(new Set());

  const currentGroupMembers = membersByGroup[activeGroup] || members;
  const selected = selectedByGroup[activeGroup] || new Set<string>();
  const setSelected = (next: Set<string>) => {
    setSelectedByGroup(prev => ({ ...prev, [activeGroup]: next }));
  };
  const [hoveredStudent, setHoveredStudent] = useState<{id: string, name: string} | null>(null);
  const [showLicenseLimitModal, setShowLicenseLimitModal] = useState(false);
  const [licenseLimitMessage, setLicenseLimitMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const getGroupDisplayName = useMemo(() => {
    return (groupKey: string) => memberGroupNames?.[groupKey] || `${groupKey}그룹`;
  }, [memberGroupNames]);

  // 초기 상태 저장용
  const [initialState, setInitialState] = useState({
    title: '',
    description: '',
    startDate: '',
    dueDate: '',
    basePoints: defaultPoints,
    icon: '',
    notes: '',
    routineDays: [] as number[],
    assignedStudentIds: [] as string[],
    rewardEnabled: false,
    includeInIncompleteCheck: false,
    rewardMessage: '',
    rewardCoins: 0,
    rewardItemCardIds: [] as string[],
    rewardCompletionRate: 100
  });

  // 변경사항 확인 함수
  const hasUnsavedChanges = useMemo(() => {
    // 초기 로딩이 완료되지 않은 경우 변경사항 없음으로 처리
    if (!isInitialLoadComplete) return false;

    // 기본 정보 변경 체크
    const hasBasicChanges =
      title !== initialState.title ||
      description !== initialState.description ||
      startDate !== initialState.startDate ||
      dueDate !== initialState.dueDate ||
      getBasePointsNum() !== initialState.basePoints ||
      icon !== initialState.icon ||
      notes !== initialState.notes ||
      JSON.stringify(routineDays.sort()) !== JSON.stringify(initialState.routineDays.sort()) ||
      rewardEnabled !== initialState.rewardEnabled ||
      includeInIncompleteCheck !== initialState.includeInIncompleteCheck ||
      rewardMessage !== initialState.rewardMessage ||
      getRewardCoinsNum() !== initialState.rewardCoins ||
      rewardCompletionRate !== initialState.rewardCompletionRate ||
      JSON.stringify(rewardItemCardIds.sort()) !== JSON.stringify(initialState.rewardItemCardIds.sort());

    // 학생 선택 변경 체크
    const currentSelectedIds = Object.values(selectedByGroup).flatMap(set => Array.from(set));
    const hasSelectionChanges =
      currentSelectedIds.length !== initialState.assignedStudentIds.length ||
      !currentSelectedIds.every(id => initialState.assignedStudentIds.includes(id)) ||
      !initialState.assignedStudentIds.every(id => currentSelectedIds.includes(id));

    return hasBasicChanges || hasSelectionChanges;
  }, [
    title, description, startDate, dueDate, basePointsDisplay, icon, notes, routineDays,
    rewardEnabled, includeInIncompleteCheck, rewardMessage, rewardCoinsDisplay, rewardItemCardIds, rewardCompletionRate,
    selectedByGroup, initialState, isInitialLoadComplete
  ]);

  // 모달 닫기 핸들러 (변경사항 확인 포함)
  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmModal(true);
    } else {
      onClose();
    }
  };

  const loadSelectedItemCards = useMemo(() => {
    return async (itemCardIds: string[]) => {
      if (itemCardIds.length === 0) {
        setSelectedItemCards([]);
        return;
      }

      try {
        const itemCardService = new ItemCardService(userId, currentClassId, currentMemberGroup);
        const itemCardSet = await itemCardService.loadItemCardSet();
        if (itemCardSet) {
          const allItems = [...itemCardSet.levelupItem, ...itemCardSet.assignmentItem, ...itemCardSet.storeItem];
          const selectedItems = allItems.filter(item => itemCardIds.includes(item.itemCardId));
          setSelectedItemCards(selectedItems);
        }
      } catch (error) {
        console.error('루틴 과제 보상 아이템 카드 로드 실패:', error);
        setSelectedItemCards([]);
      }
    };
  }, [userId, currentClassId, currentMemberGroup]);

  useEffect(() => {
    if (isOpen) {
      // 모달 열릴 때 상태 초기화
      setShowConfirmModal(false);
      document.body.style.overflow = 'hidden';
      // 오늘 날짜 기본값
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayStr = `${yyyy}-${mm}-${dd}`;

      // 시작일과 마감일 계산 (초기 상태 저장용)
      const initStart = initialData?.startDate ? new Date(initialData.startDate) : new Date(todayStr);
      const initDue = initialData?.dueDate ? new Date(initialData.dueDate) : new Date(todayStr);
      const startDateValue = isNaN(initStart.getTime()) ? todayStr : initStart.toISOString().slice(0,10);
      const dueDateValue = isNaN(initDue.getTime()) ? todayStr : initDue.toISOString().slice(0,10);

      setTitle(initialData?.title ?? '');
      setDescription(initialData?.description ?? ''); // description 초기화 추가
      setStartDate(startDateValue);
      setDueDate(dueDateValue);
      setBasePointsDisplay(initialData?.basePoints != null ? String(initialData.basePoints) : String(defaultPoints));
      setRoutineDays(initialData?.routineDays ?? []);
      setIcon(initialData?.icon ?? '');
      setNotes(initialData?.notes ?? '');
      setRewardEnabled(initialData?.rewardEnabled ?? false);
      setIncludeInIncompleteCheck(initialData?.includeInIncompleteCheck ?? false);
      setRewardMessage(initialData?.rewardMessage ?? '');
      setRewardCoinsDisplay(initialData?.rewardCoins != null ? String(initialData.rewardCoins) : '');
      setRewardCompletionRate(initialData?.rewardCompletionRate ?? 100);
      const initialRewardItems = initialData?.rewardItemCardIds ?? [];
      setRewardItemCardIds(initialRewardItems);
      setRewardCardImageErrors(new Set());
      loadSelectedItemCards(initialRewardItems);
      
      // 멤버 선택 상태 초기화 - initialData.assignedStudentIds 우선 사용
      let initialIds: string[];
      if (initialData?.assignedStudentIds && initialData.assignedStudentIds.length > 0) {
        initialIds = initialData.assignedStudentIds;
      } else if (initialSelectedIds && initialSelectedIds.length > 0) {
        initialIds = initialSelectedIds;
      } else {
        initialIds = members.map(m => m.id);
      }
      
      const primaryGroup = (currentMemberGroup || 'A').toString().trim().toUpperCase();
      setActiveGroup(primaryGroup);
      setMembersByGroup({ [primaryGroup]: members });
      setSelectedByGroup({ [primaryGroup]: new Set(initialIds) });
      setSelectionLoadedGroups(new Set(mode === 'edit' ? [primaryGroup] : []));

      // 초기 상태 저장 (변경사항 비교용)
      const dataToUse = initialData || initialData;
      setInitialState({
        title: dataToUse?.title ?? '',
        description: dataToUse?.description ?? '',
        startDate: startDateValue,
        dueDate: dueDateValue,
        basePoints: dataToUse?.basePoints ?? defaultPoints,
        icon: dataToUse?.icon ?? '',
        notes: dataToUse?.notes ?? '',
        routineDays: dataToUse?.routineDays || [],
        assignedStudentIds: initialIds,
        rewardEnabled: dataToUse?.rewardEnabled ?? false,
        includeInIncompleteCheck: dataToUse?.includeInIncompleteCheck ?? false,
        rewardMessage: dataToUse?.rewardMessage ?? '',
        rewardCoins: dataToUse?.rewardCoins ?? 0,
        rewardItemCardIds: dataToUse?.rewardItemCardIds ?? [],
        rewardCompletionRate: dataToUse?.rewardCompletionRate ?? 100
      });

      // 초기 상태 저장 (변경사항 비교용)
      setInitialState({
        title: dataToUse?.title ?? '',
        description: dataToUse?.description ?? '',
        startDate: startDateValue,
        dueDate: dueDateValue,
        basePoints: dataToUse?.basePoints ?? defaultPoints,
        icon: dataToUse?.icon ?? '',
        notes: dataToUse?.notes ?? '',
        routineDays: dataToUse?.routineDays || [],
        assignedStudentIds: initialIds,
        rewardEnabled: dataToUse?.rewardEnabled ?? false,
        includeInIncompleteCheck: dataToUse?.includeInIncompleteCheck ?? false,
        rewardMessage: dataToUse?.rewardMessage ?? '',
        rewardCoins: dataToUse?.rewardCoins ?? 0,
        rewardItemCardIds: dataToUse?.rewardItemCardIds ?? [],
        rewardCompletionRate: dataToUse?.rewardCompletionRate ?? 100
      });

      // 초기 로딩 완료 표시
      setIsInitialLoadComplete(true);
    } else {
      document.body.style.overflow = 'auto';
      // 모달이 닫힐 때 초기 로딩 상태 리셋
      setIsInitialLoadComplete(false);
      setShowConfirmModal(false);
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [isOpen, members, initialData, initialSelectedIds, mode, currentMemberGroup, defaultPoints, loadSelectedItemCards]);

  const ensureMembersLoaded = async (group: string) => {
    const g = (group || 'A').toString().trim().toUpperCase();
    if (membersByGroup[g]) return;
    try {
      const loaded = await userDataService.loadClassMembers(userId, currentClassId, g);
      setMembersByGroup(prev => ({ ...prev, [g]: loaded }));
      setSelectedByGroup(prev => ({ ...prev, [g]: prev[g] || new Set<string>() }));
    } catch (e) {
      console.error('루틴 과제 모달 - 멤버 그룹 로드 실패:', e);
      setMembersByGroup(prev => ({ ...prev, [g]: [] }));
      setSelectedByGroup(prev => ({ ...prev, [g]: prev[g] || new Set<string>() }));
    }
  };

  const ensureAssignmentSelectionLoaded = async (group: string) => {
    const g = (group || 'A').toString().trim().toUpperCase();
    if (mode !== 'edit') return;
    const assignmentId = initialData?.id as string | undefined;
    if (!assignmentId) return;
    if (selectionLoadedGroups.has(g)) return;

    try {
      const routines = await routineAssignmentService.loadRoutineAssignments(userId, currentClassId, g);
      const found = routines.find(r => r.id === assignmentId);
      const ids = Array.isArray(found?.assignedStudentIds) ? found!.assignedStudentIds : [];
      setSelectedByGroup(prev => ({ ...prev, [g]: new Set(ids) }));
    } catch (e) {
      console.error('루틴 과제 모달 - 그룹별 선택 로드 실패:', e);
    } finally {
      setSelectionLoadedGroups(prev => {
        const next = new Set(prev);
        next.add(g);
        return next;
      });
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (hoveredStudent) {
      timer = setTimeout(() => {
        setHoveredStudent(null);
      }, 2000);
    }
    
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [hoveredStudent]);

  const toggleAll = () => {
    if (selected.size === currentGroupMembers.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(currentGroupMembers.map(m => m.id)));
    }
  };

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  // 요일 토글 함수
  // dayIndex: 0=일, 1=월, 2=화, 3=수, 4=목, 5=금, 6=토
  const toggleDay = (dayIndex: number) => {
    setRoutineDays(prev => 
      prev.includes(dayIndex) 
        ? prev.filter(day => day !== dayIndex)
        : [...prev, dayIndex]
    );
  };

  const handleRoutineAssignmentSave = async () => {
    if (!title || !dueDate || routineDays.length === 0) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }
    
    // 새로 생성하거나 복사할 때만 과제 타입 제한 체크
    if (mode === 'new' || mode === 'copy') {
      const limitCheck = checkAssignmentTypeLimit(user, 'routine');
      if (!limitCheck.allowed) {
        setLicenseLimitMessage(limitCheck.message || '');
        setShowLicenseLimitModal(true);
        return;
      }
    }
    
    try {
      const groupsToApply = normalizedGroups.filter(g => (selectedByGroup[g]?.size || 0) > 0);
      if (groupsToApply.length === 0) {
        alert('최소 한 명의 멤버를 선택해주세요.');
        return;
      }

      // formData 형태로 변환
      const baseFormData: any = {
        title,
        description: description !== undefined ? description : '', // description 필드 추가
        startDate: new Date(startDate), // 시작일 필드 추가
        routineDays,
        dueDate: new Date(dueDate),
        basePoints: getBasePointsNum(),
        categoryId: 'routine',
        includeInIncompleteCheck,
        rewardEnabled,
        rewardMessage: rewardMessage || '',
        rewardCoins: getRewardCoinsNum(),
        rewardItemCardIds: rewardItemCardIds || [],
        rewardCompletionRate
      };

      // icon 필드 추가 (빈 문자열도 유효한 값으로 간주)
      if (icon !== undefined) baseFormData.icon = icon;
      // notes 필드 추가 (빈 문자열도 유효한 값으로 간주)
      if (notes !== undefined) baseFormData.notes = notes;

      if (mode === 'new') {
        const sharedId = generateAssignmentId('routine');
        for (const g of groupsToApply) {
          const formData: any = { ...baseFormData, assignedStudentIds: Array.from(selectedByGroup[g] || new Set()) };
          await routineAssignmentService.createRoutineAssignment(
            userId,
            currentClassId,
            formData,
            { generateRecords: true },
            g,
            sharedId
          );
        }
      } else if (mode === 'edit') {
        if (!initialData?.id) {
          throw new Error('수정할 루틴 과제의 ID가 없습니다.');
        }
        const routineId = initialData.id;
        for (const g of groupsToApply) {
          const formData: any = { ...baseFormData, assignedStudentIds: Array.from(selectedByGroup[g] || new Set()) };
          const groupRoutines = await routineAssignmentService.loadRoutineAssignments(userId, currentClassId, g);
          const existsInGroup = groupRoutines.some((r) => r.id === routineId);
          if (existsInGroup) {
            await routineAssignmentService.updateRoutineAssignment(
              userId,
              currentClassId,
              routineId,
              formData,
              { preserveCustomDates: true },
              g
            );
          } else {
            await routineAssignmentService.createRoutineAssignment(
              userId,
              currentClassId,
              formData,
              { generateRecords: true },
              g,
              routineId
            );
          }
        }
      } else if (mode === 'copy') {
        const sharedId = generateAssignmentId('routine');
        for (const g of groupsToApply) {
          const formData: any = { ...baseFormData, assignedStudentIds: Array.from(selectedByGroup[g] || new Set()) };
          await routineAssignmentService.createRoutineAssignment(
            userId,
            currentClassId,
            formData,
            { generateRecords: true },
            g,
            sharedId
          );
        }
      }

      onSave(mode === 'edit' && initialData?.id
        ? { ...baseFormData, id: initialData.id } as any
        : baseFormData as any);
    } catch (error) {
      console.error('❌ 루틴 과제 저장 실패:', error);
      alert(`루틴 과제 저장에 실패했습니다: ${error}`);
    }
  };

  const grouped = useMemo(() => {
    // 동적 컬럼 수에 맞춰 멤버들을 균등하게 분배
    const columnCount = currentGroupMembers.length <= 25 ? Math.ceil(currentGroupMembers.length / 5) : Math.ceil(currentGroupMembers.length / 6);
    const perCol = Math.ceil(currentGroupMembers.length / columnCount);
    const columns: ClassMember[][] = [];
    currentGroupMembers.forEach((m, idx) => {
      const colIdx = Math.floor(idx / perCol);
      if (!columns[colIdx]) columns[colIdx] = [];
      columns[colIdx].push(m);
    });
    return columns;
  }, [currentGroupMembers]);

  // 동적 컬럼 수 계산 - 멤버 수에 따라 더 적절한 열 수 계산
  const columnCount = currentGroupMembers.length <= 25 ? Math.ceil(currentGroupMembers.length / 5) : Math.ceil(currentGroupMembers.length / 6);

  const expectedTotalCount = useMemo(() => {
    if (!startDate || !dueDate || routineDays.length === 0) return 0;
    const start = new Date(startDate);
    const end = new Date(dueDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    if (startDateOnly > endDateOnly) return 0;

    let count = 0;
    const cursor = new Date(startDateOnly);
    while (cursor <= endDateOnly) {
      if (routineDays.includes(cursor.getDay())) {
        count += 1;
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return count;
  }, [startDate, dueDate, routineDays]);

  const rewardTargetCount = useMemo(() => {
    if (expectedTotalCount <= 0) return 0;
    return Math.round((expectedTotalCount * rewardCompletionRate) / 100);
  }, [expectedTotalCount, rewardCompletionRate]);

  const isRewardRestricted = user?.userType === 'free';

  if (!isOpen) return null;
  return (
    <>
      <ModalOverlay onClick={handleClose} data-modal-overlay="routine-assignment-settings">
        <ModalContent
          data-modal="routine-assignment-settings"
          onClick={(e) => e.stopPropagation()}
        >
          <Title data-modal-title>
            {mode === 'new' && '루틴 과제 생성'}
            {mode === 'copy' && '복제 과제 만들기'}
            {mode === 'edit' && '루틴 과제 수정'}
          </Title>
          {user?.userType === 'free' && (
            <RestrictionNotice>
              프리 플랜에서는 루틴 과제를 사용할 수 없습니다. 이용권을 업그레이드하세요.
            </RestrictionNotice>
          )}
          
          {/* 섹션 1: 과제 이름, 과제 설명 */}
          <FormSection>
            <Field data-block="title">
              <Label>과제 이름</Label>
              <Input 
                value={title} 
                onChange={e => setTitle(e.target.value)}
                placeholder="예: 양치하기"
                tabIndex={1}
              />
            </Field>

            <Field data-block="notes">
              <Label style={{ textAlign: 'center' }}>과제 설명 (선택사항)</Label>
              <NotesTextarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="과제에 대한 추가 설명이나 메모를 입력하세요..."
                rows={3}
              />
            </Field>
          </FormSection>

          {/* 섹션 2: 루틴 과제 완료 보상 */}
          <FormSection>
            <Field data-block="reward-toggle">
              <ToggleRow>
                <Label>루틴 과제 완료 보상</Label>
                <ToggleSwitch
                  $isOn={rewardEnabled}
                  $disabled={isRewardRestricted}
                  type="button"
                  onClick={() => {
                    if (isRewardRestricted) {
                      setLicenseLimitMessage('프리 플랜에서는 루틴 과제 완료 보상을 사용할 수 없습니다. 이용권을 업그레이드하세요.');
                      setShowLicenseLimitModal(true);
                      return;
                    }
                    setRewardEnabled(!rewardEnabled);
                  }}
                >
                  <ToggleSlider $isOn={rewardEnabled}>
                    {rewardEnabled ? 'ON' : 'OFF'}
                  </ToggleSlider>
                </ToggleSwitch>
              </ToggleRow>
            </Field>

            {rewardEnabled && (
              <>
              <Field>
                <Label>루틴 과제 완료 메세지</Label>
                <NotesTextarea
                  value={rewardMessage}
                  onChange={(e) => setRewardMessage(e.target.value)}
                  placeholder="루틴 과제 완료 시 멤버에게 표시할 안내 메시지를 입력하세요..."
                  rows={2}
                />
              </Field>

              <Field>
                <LabelRow>
                  <Label>획득 코인</Label>
                  <IncButtons>
                    <ResetBtn type="button" aria-label="코인 초기화" onClick={() => setRewardCoinsDisplay('')}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="2" y="2" width="8" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                    </ResetBtn>
                    <RoundBtn type="button" aria-label="코인 빼기" onClick={() => {
                      const n = getRewardCoinsNum();
                      if (n > 0) setRewardCoinsDisplay(String(Math.max(0, n - 10)));
                    }}>
                      <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </RoundBtn>
                    <RoundBtn type="button" aria-label="코인 더하기" onClick={() => {
                      setRewardCoinsDisplay(String(getRewardCoinsNum() + 10));
                    }}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </RoundBtn>
                  </IncButtons>
                </LabelRow>
                <Input
                  type="number"
                  min={0}
                  value={rewardCoinsDisplay}
                  onChange={(e) => setRewardCoinsDisplay(e.target.value)}
                  onBlur={() => { if (rewardCoinsDisplay === '') setRewardCoinsDisplay('0'); }}
                />
              </Field>

              <Field>
                <Label>획득 카드 설정 (루틴 과제 보상 아이템)</Label>
                <SelectedRewardsContainer>
                  {selectedItemCards.map((itemCard) => (
                    <SelectedRewardCard key={itemCard.itemCardId}>
                      <RewardCardIconArea>
                        {itemCard.cardImage === 'default' || rewardCardImageErrors.has(itemCard.itemCardId) ? (
                          <RewardCardPlaceholderIcon>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                              <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                              <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                            </svg>
                          </RewardCardPlaceholderIcon>
                        ) : itemCard.cardImage && /^\d+$/.test(itemCard.cardImage) ? (
                          <img src={`/CardIcon/IconBasic/IconBasic-${itemCard.cardImage}.png`} alt={itemCard.name} onError={() => setRewardCardImageErrors(prev => new Set(prev).add(itemCard.itemCardId))} />
                        ) : itemCard.cardImage ? (
                          <img src={itemCard.cardImage} alt={itemCard.name} onError={() => setRewardCardImageErrors(prev => new Set(prev).add(itemCard.itemCardId))} />
                        ) : itemCard.icon ? (
                          <img src={itemCard.icon} alt={itemCard.name} onError={() => setRewardCardImageErrors(prev => new Set(prev).add(itemCard.itemCardId))} />
                        ) : (
                          <RewardCardPlaceholderIcon>
                            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect width="48" height="48" rx="8" fill="var(--color-surface-highlight)"/>
                              <path d="M24 14V34M14 24H34" stroke="#999" strokeWidth="3" strokeLinecap="round"/>
                            </svg>
                          </RewardCardPlaceholderIcon>
                        )}
                      </RewardCardIconArea>
                      <RewardCardName>{itemCard.name}</RewardCardName>
                      <RewardCardMemo>{itemCard.memo}</RewardCardMemo>
                      <RemoveRewardButton
                        onClick={(e) => {
                          e.stopPropagation();
                          const newRewards = rewardItemCardIds.filter(id => id !== itemCard.itemCardId);
                          setRewardItemCardIds(newRewards);
                          loadSelectedItemCards(newRewards);
                        }}
                      >
                        ×
                      </RemoveRewardButton>
                    </SelectedRewardCard>
                  ))}
                  <AddRewardButton
                    disabled={user?.userType === 'free' || user?.userType === 'basic'}
                    onClick={() => {
                      if (user?.userType === 'free' || user?.userType === 'basic') {
                        setLicenseLimitMessage('프리/베이직 플랜에서는 아이템 카드를 과제 보상으로 사용할 수 없습니다. 이용권을 업그레이드하세요.');
                        setShowLicenseLimitModal(true);
                        return;
                      }
                      setIsItemCardModalOpen(true);
                    }}
                    type="button"
                  >
                    <AddRewardIcon>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </AddRewardIcon>
                    <AddRewardText>아이템 카드 추가</AddRewardText>
                  </AddRewardButton>
                </SelectedRewardsContainer>
              </Field>

              <Field>
                <RateLabelContainer>
                  <Label style={{ textAlign: 'center' }}>과제 완료 도달률 설정</Label>
                  <RateValue>
                    {rewardCompletionRate}% ({rewardTargetCount}/{expectedTotalCount}회)
                  </RateValue>
                </RateLabelContainer>
                <RateSliderWrapper $percentage={rewardCompletionRate}>
                  <RateSliderTrack />
                  <RateSliderFill $percentage={rewardCompletionRate} />
                  <RateSlider
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={rewardCompletionRate}
                    onChange={(e) => setRewardCompletionRate(Number(e.target.value))}
                  />
                </RateSliderWrapper>
              </Field>
            </>
            )}
          </FormSection>

          {/* 섹션 3: 시작날짜, 마감날짜, 획득 포인트, 카테고리 */}
          <FormSection>
            <Field data-block="routine-days">
              <Label>반복 요일</Label>
              <DaysContainer>
                {/* 요일 인덱스: 0=일, 1=월, 2=화, 3=수, 4=목, 5=금, 6=토 */}
                {['일', '월', '화', '수', '목', '금', '토'].map((dayName, index) => (
                  <DayButton
                    key={index}
                    type="button"
                    selected={routineDays.includes(index)}
                    onClick={() => toggleDay(index)}
                  >
                    {dayName}
                  </DayButton>
                ))}
              </DaysContainer>
            </Field>

            <FieldRow>
            <Field>
              <LabelRow>
                <Label>시작 날짜</Label>
                <IncButtons>
                  <ResetBtn type="button" aria-label="날짜 초기화" onClick={() => {
                    const today = new Date();
                    const yyyy = today.getFullYear();
                    const mm = String(today.getMonth() + 1).padStart(2, '0');
                    const dd = String(today.getDate()).padStart(2, '0');
                    const todayStr = `${yyyy}-${mm}-${dd}`;
                    setStartDate(todayStr);
                  }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="2" width="8" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </ResetBtn>
                  <RoundBtn type="button" aria-label="하루 빼기" onClick={() => {
                    if (!startDate) return; 
                    const d = new Date(startDate); 
                    d.setDate(d.getDate() - 1); 
                    setStartDate(d.toISOString().slice(0,10));
                  }}>
                    <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </RoundBtn>
                  <RoundBtn type="button" aria-label="하루 더하기" onClick={() => {
                    if (!startDate) return; 
                    const d = new Date(startDate); 
                    d.setDate(d.getDate() + 1); 
                    setStartDate(d.toISOString().slice(0,10));
                  }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </RoundBtn>
                </IncButtons>
              </LabelRow>
              <DateInputWrapper>
                {startDate && (
                  <WeekdayBadge>
                    {['일','월','화','수','목','금','토'][new Date(startDate).getDay()]}
                    {(() => {
                      const today = new Date();
                      const start = new Date(startDate);
                      
                      // 날짜만 비교하기 위해 시간 정보 제거
                      const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                      const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                      
                      const diffTime = startDateOnly.getTime() - todayDate.getTime();
                      const diffDays = diffTime / (1000 * 60 * 60 * 24);
                      
                      if (diffDays === 0) return ' (오늘)';
                      if (diffDays === 1) return ' (+1)';
                      if (diffDays === 2) return ' (+2)';
                      if (diffDays === 3) return ' (+3)';
                      if (diffDays === 4) return ' (+4)';
                      if (diffDays === 5) return ' (+5)';
                      if (diffDays === 6) return ' (+6)';
                      if (diffDays === 7) return ' (+7)';
                      if (diffDays > 7) return ` (+${diffDays})`;
                      if (diffDays === -1) return ' (-1)';
                      if (diffDays === -2) return ' (-2)';
                      if (diffDays === -3) return ' (-3)';
                      if (diffDays < -3) return ` (${diffDays})`;
                      return '';
                    })()}
                  </WeekdayBadge>
                )}
                <DateInput 
                  type="date" 
                  value={startDate} 
                  onChange={e => setStartDate(e.target.value)}
                />
              </DateInputWrapper>
            </Field>
            <Field>
              <LabelRow>
                <Label>마감 날짜</Label>
                <IncButtons>
                  <ResetBtn type="button" aria-label="날짜 초기화" onClick={() => {
                    const today = new Date();
                    const yyyy = today.getFullYear();
                    const mm = String(today.getMonth() + 1).padStart(2, '0');
                    const dd = String(today.getDate()).padStart(2, '0');
                    const todayStr = `${yyyy}-${mm}-${dd}`;
                    setDueDate(todayStr);
                  }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="2" width="8" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </ResetBtn>
                  <RoundBtn type="button" aria-label="하루 빼기" onClick={() => {
                    if (!dueDate) return; 
                    const d = new Date(dueDate); 
                    d.setDate(d.getDate() - 1); 
                    setDueDate(d.toISOString().slice(0,10));
                  }}>
                    <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </RoundBtn>
                  <RoundBtn type="button" aria-label="하루 더하기" onClick={() => {
                    if (!dueDate) return; 
                    const d = new Date(dueDate); 
                    d.setDate(d.getDate() + 1); 
                    setDueDate(d.toISOString().slice(0,10));
                  }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </RoundBtn>
                </IncButtons>
              </LabelRow>
              <DateInputWrapper>
                {dueDate && (
                  <WeekdayBadge>
                    {['일','월','화','수','목','금','토'][new Date(dueDate).getDay()]}
                    {(() => {
                      const today = new Date();
                      const due = new Date(dueDate);
                      
                      // 날짜만 비교하기 위해 시간 정보 제거
                      const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                      const dueDateOnly = new Date(due.getFullYear(), due.getMonth(), due.getDate());
                      
                      const diffTime = dueDateOnly.getTime() - todayDate.getTime();
                      const diffDays = diffTime / (1000 * 60 * 60 * 24);
                      
                      if (diffDays === 0) return ' (오늘)';
                      if (diffDays === 1) return ' (+1)';
                      if (diffDays === 2) return ' (+2)';
                      if (diffDays === 3) return ' (+3)';
                      if (diffDays === 4) return ' (+4)';
                      if (diffDays === 5) return ' (+5)';
                      if (diffDays === 6) return ' (+6)';
                      if (diffDays === 7) return ' (+7)';
                      if (diffDays > 7) return ` (+${diffDays})`;
                      if (diffDays === -1) return ' (-1)';
                      if (diffDays === -2) return ' (-2)';
                      if (diffDays === -3) return ' (-3)';
                      if (diffDays < -3) return ` (${diffDays})`;
                      return '';
                    })()}
                  </WeekdayBadge>
                )}
                <DateInput 
                  type="date" 
                  value={dueDate} 
                  onChange={e => setDueDate(e.target.value)}
                />
              </DateInputWrapper>
            </Field>
          </FieldRow>

          

          <Field>
            <LabelRow>
              <Label>획득 포인트</Label>
              <IncButtons>
                <ResetBtn type="button" aria-label="포인트 초기화" onClick={() => {
                  setBasePointsDisplay('0');
                }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="2" width="8" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                </ResetBtn>
                <RoundBtn type="button" aria-label="포인트 빼기" onClick={() => {
                  const n = getBasePointsNum();
                  if (n > 0) setBasePointsDisplay(String(Math.max(0, n - defaultPoints)));
                }}>
                  <svg width="12" height="12" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </RoundBtn>
                <RoundBtn type="button" aria-label="포인트 더하기" onClick={() => {
                  setBasePointsDisplay(String(getBasePointsNum() + defaultPoints));
                }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </RoundBtn>
              </IncButtons>
            </LabelRow>
            <Input 
              type="number" 
              min={0} 
              value={basePointsDisplay} 
              onChange={e => setBasePointsDisplay(e.target.value)}
              onBlur={() => { if (basePointsDisplay === '') setBasePointsDisplay('0'); }}
              tabIndex={2}
            />
          </Field>

          <Field>
            <Label>카테고리</Label>
            <CategoryDisplay>
              <CategoryBadge>
                <ColorDot color="#4f46e5" />
                <span>루틴</span>
              </CategoryBadge>
            </CategoryDisplay>
          </Field>
          </FormSection>

          {/* 섹션 4: 미확인 과제 확인 포함 여부 */}
          <FormSection>
            <Field data-block="include-in-incomplete-check-toggle">
              <ToggleRow>
                <Label>미확인 과제 확인시 루틴과제 제외하지 않기</Label>
                <ToggleSwitch
                  $isOn={includeInIncompleteCheck}
                  type="button"
                  onClick={() => setIncludeInIncompleteCheck(prev => !prev)}
                >
                  <ToggleSlider $isOn={includeInIncompleteCheck}>
                    {includeInIncompleteCheck ? 'ON' : 'OFF'}
                  </ToggleSlider>
                </ToggleSwitch>
              </ToggleRow>
            </Field>
          </FormSection>

          {/* 섹션 5: 과제를 부여할 멤버 선택 */}
          <FormSection>
            <SelectHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>과제를 부여할 멤버 선택</span>
                {hoveredStudent && (
                  <SelectedStudentBadge key={hoveredStudent.id + Date.now()}>
                    {hoveredStudent.name}
                  </SelectedStudentBadge>
                )}
              </div>
              <SelectAllButton onClick={toggleAll}>{selected.size === currentGroupMembers.length ? '전체 해제' : '전체 선택'}</SelectAllButton>
            </SelectHeader>

            {normalizedGroups.length > 1 && (
              <GroupControls>
                <GroupTabs>
                  {normalizedGroups.map((g) => (
                    <GroupTabButton
                      key={g}
                      type="button"
                      $active={activeGroup === g}
                      onClick={async () => {
                        setActiveGroup(g);
                        await ensureMembersLoaded(g);
                        await ensureAssignmentSelectionLoaded(g);
                      }}
                    >
                      {getGroupDisplayName(g)}
                    </GroupTabButton>
                  ))}
                </GroupTabs>
              </GroupControls>
            )}

            <StudentsGrid $columnCount={columnCount}>
            {grouped.map((col, colIdx) => (
              <Column key={colIdx}>
                {col.map(m => (
                  <div key={m.id} style={{ position: 'relative' }}>
                    <StudentButton
                      onClick={() => {
                        toggle(m.id);
                        setHoveredStudent(hoveredStudent?.id === m.id ? null : m);
                      }}
                      aria-pressed={selected.has(m.id)}
                      title={m.name}
                    >
                      <span className="num">{String(m.number).padStart(2, '0')}</span>
                      <span className="name">{m.name}</span>
                    </StudentButton>
                  </div>
                ))}
              </Column>
            ))}
          </StudentsGrid>
          </FormSection>

          <Buttons>
            <CancelButton data-variant="modal-secondary" onClick={handleClose}>취소</CancelButton>
            <SaveButton 
              onClick={handleRoutineAssignmentSave} 
              tabIndex={3}
              disabled={!title.trim() || normalizedGroups.every(g => (selectedByGroup[g]?.size || 0) === 0) || routineDays.length === 0 || user?.userType === 'free'}
            >
              저장
            </SaveButton>
          </Buttons>
        </ModalContent>
      </ModalOverlay>
      
      <LicenseLimitModal
        isOpen={showLicenseLimitModal}
        message={licenseLimitMessage}
        onClose={() => setShowLicenseLimitModal(false)}
        onUpgrade={() => {
          setShowLicenseLimitModal(false);
          onLicenseClick?.();
        }}
      />

      {/* 변경사항 확인 모달 */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          setShowConfirmModal(false);
          onClose();
        }}
        title={['변경된 데이터가 있습니다.', '팝업을 닫으시겠습니까?']}
        confirmText="닫기"
        cancelText="취소"
        icon="!"
        iconBackground="var(--status-pending)"
      />

      <ItemCardSelectionModal
        isOpen={isItemCardModalOpen}
        onClose={() => setIsItemCardModalOpen(false)}
        onConfirm={(selectedItemCardIds) => {
          setRewardItemCardIds(selectedItemCardIds);
          loadSelectedItemCards(selectedItemCardIds);
          setIsItemCardModalOpen(false);
        }}
        userId={userId}
        classId={currentClassId}
        initialSelectedIds={rewardItemCardIds}
        categoryFilter={['assignment']}
      />

    </>
  );
};

export default RoutineAssignmentSettingsModal;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding: 40px 20px 100px 20px;
  box-sizing: border-box;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 20px 10px 50px 10px;
  }
`;
const ModalContent = styled.div<{ $isRestricted?: boolean }>`
  background: var(--color-surface); border-radius: 12px; padding: 30px 20px; width: 100%; max-width: 720px; box-shadow: var(--modal-shadow); border: var(--modal-border);
  ${props => props.$isRestricted && `
    filter: grayscale(100%);
    pointer-events: none;
    user-select: none;
  `}

  @media (max-width: 768px) {
    padding: 20px 16px;
    margin: 20px 0;
  }
`;
const Title = styled.h3`
  margin: 0 0 16px; text-align: center; font-size: 1.2rem; color: var(--color-text-primary);
`;

const RestrictionNotice = styled.div`
  text-align: center;
  padding: 12px 16px;
  margin: -8px 0 16px 0;
  background: var(--color-accent-soft);
  border: 1px solid var(--color-accent);
  border-radius: 6px;
  color: var(--color-accent-text);
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.4;
`;

const Field = styled.div`
  display: flex; flex-direction: column; gap: 6px; margin-bottom: 10px;
  
  ${FormSection} &:last-child {
    margin-bottom: 0;
  }
`;
const FieldRow = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px;
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;
const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a1a;
  
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;
const Input = styled.input`
  padding: 8px 10px; 
  border: 1px solid var(--color-border); 
  border-radius: 6px; 
  font-size: 1rem; 
  min-width: 0; 
  height: 38px; 
  box-sizing: border-box;
  text-align: center;
  background: var(--color-surface);
  color: var(--color-text-primary);
  &:focus-visible { outline: 2px solid #000; outline-offset: 2px; }
  
  :root[data-theme='light'] & {
    border-color: var(--color-section-border);
  }
`;

const LabelRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const ToggleSwitch = styled.button<{ $isOn: boolean; $disabled?: boolean }>`
  width: 70px;
  height: 28px;
  min-width: 70px;
  min-height: 28px;
  border-radius: 14px;
  background: ${props => props.$isOn ? 'var(--color-toggle-on-bg)' : 'var(--color-toggle-off-bg)'};
  border: none;
  cursor: pointer;
  position: relative;
  transition: background 0.3s ease;
  opacity: ${props => props.$disabled ? 0.7 : 1};
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ToggleSlider = styled.div<{ $isOn: boolean }>`
  width: 34px;
  height: 22px;
  border-radius: 11px;
  background: var(--color-surface);
  position: absolute;
  top: 3px;
  left: ${props => props.$isOn ? '34px' : '3px'};
  transition: left 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: ${props => props.$isOn ? 'var(--color-toggle-on-text)' : 'var(--color-toggle-off-text)'};
`;

const SelectedRewardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, 120px);
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  min-height: 220px;
  justify-content: center;
  
  :root[data-theme='light'] & {
    border-color: var(--color-section-border);
  }
`;

const SelectedRewardCard = styled.div`
  width: 120px;
  height: 200px;
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10%;
  position: relative;
  box-sizing: border-box;
  transition: all 0.2s ease;

  /* 다크 모드: 흰색 테두리 */
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.8);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RewardCardIconArea = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-panel);
  border: 2px solid var(--color-accent-strong);

  /* 다크 모드: 흰색 테두리 */
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.8);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RewardCardPlaceholderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RewardCardName = styled.div`
  width: 100%;
  margin-top: 8%;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  line-height: 1.3;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RewardCardMemo = styled.div`
  width: 100%;
  margin-top: 4%;
  margin-bottom: auto;
  font-size: 8px;
  color: #999;
  text-align: center;
  word-break: break-word;
  line-height: 1.3;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 0;
`;

const RemoveRewardButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: var(--color-accent-strong);
  border: none;
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 1;

  /* 다크 모드: 흰색 배경에 회색 X */
  :root:not([data-theme='light']) & {
    background: rgba(255, 255, 255, 1);
    color: #333333;
  }

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.8);
    transform: scale(1.1);
  }

  /* 다크 모드: 호버 시에도 흰색 유지 */
  :root:not([data-theme='light']) &:hover {
    background: rgba(255, 255, 255, 0.9);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const AddRewardButton = styled.button<{ disabled?: boolean }>`
  width: 120px;
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px;
  background: ${props => props.disabled ? 'var(--color-surface)' : 'var(--color-surface)'};
  border: 2px dashed ${props => props.disabled ? 'var(--color-border)' : 'var(--color-accent-strong)'};
  border-radius: 8px;
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-accent-text)'};
  font-size: 12px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  box-sizing: border-box;
  flex-shrink: 0;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover:not(:disabled) {
    background: var(--color-accent-soft);
    border-color: rgba(var(--color-accent-rgb), 0.8);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const AddRewardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  border: 2px dashed var(--color-accent-strong);
  border-radius: 50%;
  background: var(--color-accent-soft);
  flex-shrink: 0;
`;

const AddRewardText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--color-accent-text);
  text-align: center;
  line-height: 1.3;
  word-break: keep-all;
  flex-shrink: 0;
`;

const RateLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const RateValue = styled.span`
  font-weight: 700;
  color: var(--color-accent-strong);
  text-align: center;

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const THUMB_SIZE = 18;
const TRACK_HEIGHT = 8;

const RateSliderWrapper = styled.div<{ $percentage: number }>`
  position: relative;
  width: 100%;
  height: ${THUMB_SIZE}px;
  display: flex;
  align-items: center;
`;

const RateSliderTrack = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: ${TRACK_HEIGHT}px;
  top: 50%;
  transform: translateY(-50%);
  background: var(--color-border);
  border-radius: 6px;
  z-index: 0;
`;

const RateSliderFill = styled.div<{ $percentage?: number }>`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: ${props => props.$percentage || 0}%;
  height: ${TRACK_HEIGHT}px;
  background: var(--color-accent-strong);
  border-radius: 6px 0 0 6px;
  z-index: 1;
  pointer-events: none;
  
  :root:not([data-theme='light']) & {
    background: #ffffff;
  }
`;

const RateSlider = styled.input`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  appearance: none;
  background: transparent;
  outline: none;
  cursor: pointer;
  z-index: 2;
  
  &::-webkit-slider-runnable-track {
    height: ${THUMB_SIZE}px;
    background: transparent;
  }
  
  &::-webkit-slider-thumb {
    appearance: none;
    width: ${THUMB_SIZE}px;
    height: ${THUMB_SIZE}px;
    border-radius: 50%;
    background: var(--color-accent-strong);
    border: 2px solid white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
    cursor: pointer;
  }
  
  :root:not([data-theme='light']) &::-webkit-slider-thumb {
    background: #ffffff;
    border: 2px solid var(--color-border);
  }
  
  &::-moz-range-track {
    height: ${THUMB_SIZE}px;
    background: transparent;
  }
  
  &::-moz-range-thumb {
    width: ${THUMB_SIZE}px;
    height: ${THUMB_SIZE}px;
    border-radius: 50%;
    background: var(--color-accent-strong);
    border: 2px solid white;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  }
  
  :root:not([data-theme='light']) &::-moz-range-thumb {
    background: #ffffff;
    border: 2px solid var(--color-border);
  }
`;
const IncButtons = styled.div`
  display: flex; gap: 2px; flex-shrink: 0;
`;
const RoundBtn = styled.button`
  padding: 4px 8px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface)fff;
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 2px 6px;
    height: 24px;
    min-height: 24px;
    font-size: 0.8rem;
  }

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  svg {
    color: var(--color-text-primary);
  }
`;

const ResetBtn = styled(RoundBtn)`
  svg {
    rect,
    path {
      stroke: var(--color-text-primary);
    }
  }
`;

const DateInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 8px 10px;
  background: var(--color-surface);
  height: 38px;
  box-sizing: border-box;
  
  :root[data-theme='light'] & {
    border-color: var(--color-section-border);
  }
`;

const DateInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-size: 1rem;
  flex: 1;
  min-width: 0;
  color: var(--color-text-primary);
  
  /* 브라우저 기본 달력 아이콘 스타일링 - 다크 모드에서만 흰색으로 */
  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 1;
    filter: var(--date-calendar-icon-filter);
  }
  
  &::-moz-calendar-picker-indicator {
    cursor: pointer;
    opacity: 1;
    filter: var(--date-calendar-icon-filter);
  }
  
  &:focus-visible { outline: 2px solid #000; outline-offset: 2px; }
`;

const WeekdayBadge = styled.span`
  background-color: var(--weekday-badge-bg);
  color: var(--weekday-badge-text-color);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: var(--weekday-badge-text-weight);
  white-space: nowrap;
  flex-shrink: 0;
`;

const DaysContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 20px;
  
  @media (max-width: 480px) {
    gap: 6px;
  }
  
  @media (max-width: 360px) {
    gap: 4px;
  }
`;

const DayButton = styled.button<{ selected: boolean }>`
  width: 36px;
  height: 36px;
  min-width: 26px;
  min-height: 26px;
  border: 2px solid ${props => props.selected ? 'transparent' : 'var(--color-border)'};
  border-radius: 50%;
  background: ${props => props.selected ? 'var(--color-accent-strong)' : 'var(--color-panel)'};
  color: ${props => props.selected ? 'white' : 'var(--color-text-gray)'};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: bold;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  /* 다크모드: 텍스트 색상 및 배경색 변경 */
  :root:not([data-theme='light']) & {
    color: ${props => props.selected ? '#ffffff' : 'rgba(255, 255, 255, 0.5)'};
    /* 다크모드: 선택되지 않았을 때 연한 테두리, 선택되었을 때 흰색 테두리 */
    border: 2px solid ${props => props.selected ? '#ffffff' : 'rgba(255, 255, 255, 0.45)'};
    /* 다크모드: 비선택일 때 연한 회색 배경 (선택했을 때 색상과 같은 색이지만 연하게) */
    background: ${props => props.selected ? 'var(--color-accent-strong)' : 'var(--color-accent-soft)'};
  }

  @media (max-width: 480px) {
    width: 26px;
    height: 26px;
    font-size: 0.7rem;
  }
  
  &:hover {
    background: ${props => props.selected ? 'var(--color-accent)' : 'var(--color-border)'};
  }
  
  &:focus {
    outline: none;
  }
`;

const CategoryDisplay = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const CategoryBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  color: var(--color-text-secondary);
  font-weight: 500;
  width: 100%;
  justify-content: center;
`;

const ColorDot = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.color};
  flex-shrink: 0;
`;

const SelectHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin: 8px 0;
`;
const SelectAllButton = styled.button`
  padding: 6px 10px; background: var(--color-surface); border: 2px solid var(--color-border); border-radius: 8px; font-weight: 600; cursor: pointer;
  color: var(--color-text-primary);
  &:focus-visible { outline: 2px solid #000; outline-offset: 2px; }
`;

const StudentsGrid = styled.div<{ $columnCount: number }>`
  display: grid; 
  grid-template-columns: repeat(${props => props.$columnCount}, auto);
  gap: 6px; 
  align-items: start; 
  justify-items: center;
  justify-content: center;
  padding: 8px; 
  border: 2px solid var(--color-border); 
  border-radius: 8px; 
  background: var(--color-panel);
  width: 100%;
  max-height: 300px;
  overflow-x: auto;
  overflow-y: auto;
  margin: 0 auto;
`;

const Column = styled.div`
  display: grid; 
  grid-auto-rows: minmax(36px, auto); 
  gap: 6px;
`;

const StudentButton = styled.button<{ disabled?: boolean }>`
  display: flex; 
  align-items: center; 
  gap: 4px; 
  padding: 6px 8px; 
  background: var(--color-surface); 
  border: 1px solid var(--color-border); 
  border-radius: 8px; 
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  width: 100%;
  min-height: 36px;
  box-sizing: border-box;
  transition: all 0.2s ease;
  box-shadow: none;
  
  .num { 
    font-weight: 700; 
    color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-border)'};
    font-size: 16px;
    flex-shrink: 0;
  }
  .name { 
    display: inline; 
    color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-text-muted)'};
    font-weight: 500;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
  }
  
  @media (max-width: 600px) { 
    .name { 
      font-size: 14px;
    }
  }
  
  @media (max-width: 520px) { 
    justify-content: center;
    .name { display: none; } 
  }
  
  /* 선택된 상태 */
  &[aria-pressed="true"] {
    background: var(--color-surface);
    border-color: var(--color-accent-text);
    border-width: 1px;
    box-shadow: none;

    .num {
      color: var(--color-accent-text);
      font-weight: 700;
    }
    .name {
      color: var(--color-text-primary);
      font-weight: 600;
    }
  }
  
  /* 호버 효과 */
  &:hover {
    background-color: ${props => props.disabled ? 'var(--color-surface)' : 'var(--color-border)'};
  }
  
  /* 선택된 상태에서 호버 시 테두리 유지 */
  &[aria-pressed="true"]:hover {
    border-color: var(--color-accent-text);
    border-width: 1px;
    box-shadow: none;
  }
  
  &:focus-visible { 
    outline: 2px solid #000; 
    outline-offset: 2px; 
  }
`;

const SelectedStudentBadge = styled.span`
  background-color: var(--color-student-badge-bg);
  color: var(--color-student-badge-text);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 8px;
  animation: slideInFromRight 0.3s ease-out, fadeOut 0.5s ease-in 1.5s forwards;
  
  @keyframes slideInFromRight {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: translateX(0);
    }
    to {
      opacity: 0;
      transform: translateX(0);
    }
  }
`;

const Buttons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: ${props => props.disabled ? 'var(--color-save-button-disabled-bg)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-save-button-disabled-text)' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const NotesTextarea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 60px;
  text-align: center;
  background: var(--color-surface);
  color: var(--color-text-primary);

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  :root[data-theme='light'] & {
    border-color: var(--color-section-border);
  }
`;